import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-yearly-view-per-location-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './yearly-view-per-location-chart.component.html',
  styleUrls: ['./yearly-view-per-location-chart.component.css'],
})
export class YearlyViewPerLocationChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  months = ['Jan 24', 'Feb 24', 'Mar 24', 'Apr 24', 'May 24', 'Jun 24', 'Jul 24', 'Aug 24', 'Sep 24', 'Oct 24', 'Nov 24', 'Dec 24'];
  labels = ['Jan 24', 'Feb 24', 'Mar 24', 'Apr 24', 'May 24', 'Jun 24', 'Jul 24', 'Aug 24', 'Sep 24', 'Oct 24', 'Nov 24', 'Dec 24'];

  totalCostsLabel: string = "Total Costs (EUR)";
  electricityActualLabel: string = "Electricity Actual (In kWh)";
  gasActualLabel: string = "Gas Actual (In smc)";
  electricityForecastLabel: string = "Electricity Forecast (In kWh)";
  gasForecastLabel: string = "Gas Forecast (In kWh)";

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyelectricityActualData = [200, 240, 300, 260, 200, 210, 220, 0, 0, 0, 0, 0];
  dummygasActualData = [150, 160, 170, 180, 190, 200, 180, 0, 0, 0, 0, 0];
  dummytotalCostsData = [2900, 2920, 3017, 2900, 2700, 2800, 2500, 2700, 2800, 2900, 3000, 3010];
  dummyelectricityForecastData = [0, 0, 0, 0, 0, 0, 0, 240, 260, 290, 300, 310];
  dummygasForecastData = [0, 0, 0, 0, 0, 0, 0, 190, 210, 220, 230, 240];

  @Input() electricityActualData = [200, 240, 300, 260, 200, 210, 220, 0, 0, 0, 0, 0];
  @Input() gasActualData = [150, 160, 170, 180, 190, 200, 180, 0, 0, 0, 0, 0];
  @Input() totalCostsData = [2900, 2920, 3017, 2900, 2700, 2800, 2500, 2700, 2800, 2900, 3000, 3010];

  @Input() electricityForecastData = [0, 0, 0, 0, 0, 0, 0, 240, 260, 290, 300, 310];
  @Input() gasForecastData = [0, 0, 0, 0, 0, 0, 0, 190, 210, 220, 230, 240];

  @Input() chartDisplayYear = new Date().getFullYear();

  @Input() fromDate?: Date;

  @Input() showElectricityForecastDataset: boolean = false;
  @Input() showGasForecastDataset: boolean = false;

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.populateLabels();
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.electricity_actual'),
      this.translocoService.selectTranslate('dashboard.gas_actual'),
      this.translocoService.selectTranslate('dashboard.electricity_forecast'),
      this.translocoService.selectTranslate('dashboard.gas_forecast')
    ]).subscribe((
      [
        electricityActualTranslation,
        gasActualTranslation,
        electricityForecastTranslation,
        gasForecastTranslation
      ]
    ) => {
      this.electricityActualLabel = electricityActualTranslation;
      this.gasActualLabel = gasActualTranslation;
      this.electricityForecastLabel = electricityForecastTranslation;
      this.gasForecastLabel = gasForecastTranslation;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.months = translations;
      this.populateLabels();
      this.updateChart();
    });
  }

  populateLabels(): void {
    if (!this.fromDate) {
      this.labels = this.months;
    } else {
      const startMonthIndex = this.fromDate.getMonth();

      this.labels = [
        ...this.months.slice(startMonthIndex),
        ...this.months.slice(0, startMonthIndex),
      ];
    }
  }


  toggleDataset(index: number, hidden: boolean | null = null): void {
    if (this.data && this.data.datasets && this.data.datasets[index]) {
      if (hidden === null) {
        this.data.datasets[index].hidden = !this.data.datasets[index].hidden;
      }
      else {
        this.data.datasets[index].hidden = hidden;
      }
      this.data = { ...this.data };
    }
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.electricityActualData = this.dummyelectricityActualData;
      this.gasActualData = this.dummygasActualData;
      this.totalCostsData = this.dummytotalCostsData;
      this.electricityForecastData = this.dummyelectricityForecastData;
      this.gasForecastData = this.dummygasForecastData;
      this.chartDisplayYear = new Date().getFullYear();
    }
    Chart.register(ChartDataLabels);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    this.data = {
      labels: this.labels,
      datasets: [
        {
          label: this.electricityActualLabel,
          type: 'bar',
          backgroundColor: '#A9BE92',
          data: this.electricityActualData,
          borderRadius: 5,
          datalabels: {
            display: (ctx: any) => {
              const dataIndex = ctx.dataIndex;
              if (!this.labels || !this.labels[dataIndex]) return false;
              return true;
            },
            align: 'center',
            anchor: 'center',
            color: '#000000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            textAlign: 'center',
            formatter: (value: any) => { if (!value || value === 0) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0') || value}\nkWh` },
          },
        },
        {
          label: this.gasActualLabel,
          type: 'bar',
          backgroundColor: '#80A7A5',
          data: this.gasActualData,
          borderRadius: 5,
          datalabels: {
            display: (ctx: any) => {
              const dataIndex = ctx.dataIndex;
              if (!this.labels || !this.labels[dataIndex]) return false;
              return true;
            },

            align: 'center',
            anchor: 'center',
            color: '#000000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            textAlign: 'center',
            formatter: (value: any) => { if (!value || value === 0) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0') || value}\nsmc` },
          },
        }
      ],
    };

    if (this.showElectricityForecastDataset) {
      this.data.datasets.push({
        label: this.electricityForecastLabel,
        type: 'bar',
        backgroundColor: '#C9D6BB',
        data: this.electricityForecastData,
        borderRadius: 5,
      },);
    }

    if (this.showGasForecastDataset) {
      this.data.datasets.push({
        label: this.gasForecastLabel,
        type: 'bar',
        backgroundColor: '#C0D3D2',
        data: this.gasForecastData,
        borderRadius: 5,
      },);
    }

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            font: {
              size: 10,
            },
          },
        },
        datalabels: {
          display: (ctx: any) => {
            const dataIndex = ctx.dataIndex;
            const datasetLabel = ctx.dataset.label;

            if (!this.labels || !this.labels[dataIndex]) return false;

            const isCurrentMonth = dataIndex === currentMonth;
            const isCurrentYear = this.chartDisplayYear === currentYear;

            return datasetLabel === 'Total Costs (EUR)' && isCurrentMonth && isCurrentYear;
          },
          align: 'top',
          anchor: 'end',
          color: '#000000',
          font: {
            size: 10,
            weight: 'normal',
            family: 'Gilroy',
          },
          formatter: (value: any) => {
            return `${this.decimalPipe.transform(value, '1.0-0') || value} €`;
          },
        },
        tooltip: {
          callbacks: {
            label: this.formatTooltipLabel.bind(this)
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
        },
        y: {
          display: false,
          beginAtZero: true,
          stacked: true,
        },
        y1: {
          display: false,
          beginAtZero: true,
        },
      },
    };
  }

  formatTooltipLabel(tooltipItem: any) {
    let label = tooltipItem.dataset.label || '';
    let value = tooltipItem.raw || 0;
    return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}`;
  }
}
